import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Copy as CopyIcon, Download, FileText, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DownloadTestDialog } from "@/components/DownloadTestDialog";
import { useCreateTest } from "@/hooks/use-create-test";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { readingLevelDisplayLabel, type Test } from "@shared/schema";

// Absolute calendar date for a saved test's createdAt, e.g. "Jun 12, 2026".
const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

/**
 * Saved-test rows plus their per-row actions (open, download, rename,
 * duplicate, delete). Used by Home (recent tests) and My Tests (full list);
 * callers pass tests already filtered and sorted in display order.
 */
export function TestList({ tests }: { tests: Test[] }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { atTestLimit, showLimitToast } = useCreateTest();

  // Per-row action state. Holds the test the dialog is acting on; the
  // dialog opens whenever this is non-null.
  const [downloadTarget, setDownloadTarget] = useState<Test | null>(null);
  const [renameTarget, setRenameTarget] = useState<Test | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Test | null>(null);

  // Open routes to /tests/:id (the dedicated preview surface). Crucially
  // this doesn't call loadSavedState() — that would clobber a workflow
  // draft sitting in TestContext / localStorage. The preview + edit pages
  // fetch the test from the server independently.
  const handleOpen = (test: Test) => setLocation(`/tests/${test.id}`);

  const renameMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const res = await apiRequest("PATCH", `/api/tests/${id}`, { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tests"] });
      toast({ title: "Renamed", description: "Test name updated." });
      setRenameTarget(null);
    },
    onError: () => {
      toast({ title: "Rename failed", description: "Please try again.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/tests/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tests"] });
      toast({ title: "Deleted", description: "Test removed from your list." });
      setDeleteTarget(null);
    },
    onError: () => {
      toast({ title: "Delete failed", description: "Please try again.", variant: "destructive" });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/tests/${id}/duplicate`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tests"] });
      toast({ title: "Duplicated", description: "A copy of the test was created." });
    },
    onError: () => {
      toast({ title: "Duplicate failed", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleDuplicate = (id: string) => {
    if (atTestLimit) {
      showLimitToast();
      return;
    }
    duplicateMutation.mutate(id);
  };

  const startRename = (test: Test) => {
    setRenameValue(test.name);
    setRenameTarget(test);
  };

  const confirmRename = () => {
    if (!renameTarget) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      toast({
        title: "Name required",
        description: "A test needs a name.",
        variant: "destructive",
      });
      return;
    }
    if (trimmed === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    renameMutation.mutate({ id: renameTarget.id, name: trimmed });
  };

  return (
    <>
      <ul className="flex flex-col gap-2">
        {tests.map((test, index) => {
          const subject = test.config?.subject;
          const readingLevel = test.config?.readingLevel;
          // Each detail is its own element so it can be matched on its own
          // (the reading-level e2e checks exact text per detail).
          const meta = [
            subject,
            readingLevel && `Reading level ${readingLevelDisplayLabel(readingLevel)}`,
            `${test.questions.length} questions`,
            formatDate(test.createdAt),
          ].filter(Boolean) as string[];
          return (
            <li
              key={test.id}
              className="group flex cursor-pointer items-center gap-4 rounded-xl border bg-card p-3.5 shadow-xs transition-colors hover:border-brand-300 hover:bg-brand-50/40"
              data-testid={`test-${index}`}
              onClick={() => handleOpen(test)}
            >
              <div
                aria-hidden="true"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:bg-brand-100 group-hover:text-brand-600"
              >
                <FileText className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{test.name}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-sm text-muted-foreground">
                  {meta.map((item, i) => (
                    <span key={item} className="flex items-center gap-x-1.5">
                      {i > 0 && <span aria-hidden="true">·</span>}
                      <span>{item}</span>
                    </span>
                  ))}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpen(test);
                  }}
                  data-testid={`button-open-${index}`}
                >
                  Open
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Test actions"
                      data-testid={`button-actions-${index}`}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onSelect={() => setDownloadTarget(test)}>
                      <Download className="mr-2 h-4 w-4" />
                      Download…
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => startRename(test)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Rename…
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => handleDuplicate(test.id)}
                      disabled={duplicateMutation.isPending}
                    >
                      <CopyIcon className="mr-2 h-4 w-4" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => setDeleteTarget(test)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </li>
          );
        })}
      </ul>

      <DownloadTestDialog
        open={!!downloadTarget}
        onOpenChange={(o) => !o && setDownloadTarget(null)}
        test={downloadTarget}
      />

      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename test</DialogTitle>
            <DialogDescription>Give this test a new name.</DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Test name"
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmRename();
            }}
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRenameTarget(null)}
              disabled={renameMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={confirmRename} disabled={renameMutation.isPending}>
              {renameMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this test?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `“${deleteTarget.name}” will be permanently removed. This can't be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
